import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Loader2, Sparkles } from "lucide-react";
import { useNotify } from "ra-core";
import { Fragment, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { Card } from "./FunnelCard";
import type { DayRange } from "./performance";

interface Report {
  id: number;
  kind: "weekly" | "question";
  period_from: string;
  period_to: string;
  question: string | null;
  body: string;
  created_at: string;
}

const SUGGESTIONS = [
  "¿Qué anuncio trae ventas y cuál solo trae curiosos?",
  "¿Qué tipo de lead cierra mejor y cuál no vale la pena?",
  "¿Cómo va cada setter y qué tiene que mejorar?",
  "¿A qué hora y con qué rapidez conviene llamar?",
];

const MAX_QUESTION = 600;

const callAnalyst = async (body: Record<string, unknown>) => {
  const { data, error } = await getSupabaseClient().functions.invoke(
    "performance_report",
    { method: "POST", body },
  );
  if (error) {
    const context = (error as { context?: Response }).context;
    const detail = context
      ? await context
          .json()
          .then((json: { message?: string }) => json?.message)
          .catch(() => null)
      : null;
    throw new Error(detail || error.message);
  }
  return data as {
    report: Report;
    emailed?: number;
    mail_error?: string | null;
  };
};

const shortDay = (day: string) =>
  new Date(`${day}T12:00:00`).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
  });

/** **bold** inside a line, as React nodes (no HTML injection). */
const inline = (text: string): ReactNode[] =>
  text
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, index) =>
      part.startsWith("**") && part.endsWith("**") ? (
        <b key={index}>{part.slice(2, -2)}</b>
      ) : (
        <Fragment key={index}>{part}</Fragment>
      ),
    );

/** The analyst's light markdown: "## " titles, "- " lists, **bold**. */
export const ReportText = ({ body }: { body: string }) => {
  const blocks: ReactNode[] = [];
  let list: string[] = [];
  const flush = () => {
    if (!list.length) return;
    const items = list;
    blocks.push(
      <ul key={`ul-${blocks.length}`} className="ml-5 list-disc space-y-1">
        {items.map((item, index) => (
          <li key={index}>{inline(item)}</li>
        ))}
      </ul>,
    );
    list = [];
  };
  body.split("\n").forEach((raw, index) => {
    const line = raw.trim();
    if (line.startsWith("- ")) {
      list.push(line.slice(2));
      return;
    }
    flush();
    if (!line) return;
    if (line.startsWith("#")) {
      blocks.push(
        <h3 key={index} className="mt-3 text-sm font-semibold first:mt-0">
          {inline(line.replace(/^#+\s*/, ""))}
        </h3>,
      );
    } else {
      blocks.push(<p key={index}>{inline(line)}</p>);
    }
  });
  flush();
  return (
    <div className="flex flex-col gap-1.5 text-sm leading-relaxed">
      {blocks}
    </div>
  );
};

/** Claude reads the numbers: weekly report and answers to questions. */
export const AnalystCard = ({ range }: { range: DayRange }) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [question, setQuestion] = useState("");
  const [openId, setOpenId] = useState<number | null>(null);

  const reports = useQuery({
    queryKey: ["gula-reports"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("gula_reports")
        .select("id, kind, period_from, period_to, question, body, created_at")
        .order("created_at", { ascending: false })
        .limit(12);
      if (error) throw error;
      return (data ?? []) as Report[];
    },
  });

  const run = useMutation({
    mutationFn: callAnalyst,
    onSuccess: (data, variables) => {
      setOpenId(data.report.id);
      queryClient.invalidateQueries({ queryKey: ["gula-reports"] });
      if (variables.action === "ask") setQuestion("");
      if (variables.action === "weekly")
        notify(
          data.mail_error
            ? "Informe hecho, pero no se ha podido enviar el correo"
            : `Informe hecho${data.emailed ? " y enviado por correo" : ""}`,
          { type: data.mail_error ? "warning" : "success" },
        );
    },
    onError: (error: unknown) =>
      notify(error instanceof Error ? error.message : "No se ha podido hacer", {
        type: "error",
      }),
  });

  const ask = (text: string) => {
    const clean = text.trim();
    if (clean.length < 3 || run.isPending) return;
    run.mutate({
      action: "ask",
      question: clean,
      from: range.from,
      to: range.to,
    });
  };

  const list = reports.data ?? [];
  const open = list.find((report) => report.id === openId) ?? list[0];

  return (
    <Card
      title="Analista"
      hint="Claude lee estos números y te dice qué hacer. Solo lee: no cambia nada del CRM"
    >
      <div className="flex flex-col gap-2">
        <Textarea
          value={question}
          maxLength={MAX_QUESTION}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder="Pregúntale lo que quieras sobre el periodo elegido…"
          rows={2}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              ask(question);
            }
          }}
        />
        <div className="flex flex-wrap items-center gap-1.5">
          {SUGGESTIONS.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              disabled={run.isPending}
              onClick={() => ask(suggestion)}
              className="rounded-full border px-3 py-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              {suggestion}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={run.isPending || question.trim().length < 3}
            onClick={() => ask(question)}
          >
            {run.isPending && run.variables?.action === "ask" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Preguntar
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={run.isPending}
            onClick={() => run.mutate({ action: "weekly" })}
            title="El informe de la última semana completa. Llega solo cada lunes por la mañana"
          >
            {run.isPending && run.variables?.action === "weekly" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            Informe semanal ahora
          </Button>
        </div>
        {run.isPending ? (
          <p className="text-xs text-muted-foreground">
            Analizando los números… tarda unos segundos.
          </p>
        ) : null}
      </div>

      {open ? (
        <div className="rounded-2xl bg-muted p-4">
          <p className="mb-2 text-xs text-muted-foreground">
            {open.kind === "weekly" ? "Informe semanal" : `«${open.question}»`}{" "}
            · {shortDay(open.period_from)} – {shortDay(open.period_to)}
          </p>
          <ReportText body={open.body} />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Cada lunes por la mañana llega aquí (y a vuestro correo) el informe de
          la semana anterior.
        </p>
      )}

      {list.length > 1 ? (
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium text-muted-foreground">
            Anteriores
          </p>
          {list.map((report) => (
            <button
              key={report.id}
              type="button"
              onClick={() => setOpenId(report.id)}
              className={cn(
                "truncate rounded-lg px-2 py-1 text-left text-xs hover:bg-muted",
                report.id === open?.id
                  ? "bg-muted font-medium"
                  : "text-muted-foreground",
              )}
            >
              {report.kind === "weekly"
                ? `Informe semanal ${shortDay(report.period_from)} – ${shortDay(report.period_to)}`
                : report.question}
            </button>
          ))}
        </div>
      ) : null}
    </Card>
  );
};
